
import { ContentGridView, ScrollConfiguration } from 'components/ContentGridView'
import { NewFeedMessage } from 'components/FeedView'
import {
  AnyContent,
  ContentItem,
  ContentSource,
  Feed,
  INTERACTION_EVENT_OPEN,
  RangeFilter,
  Stream
} from 'interfaces'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMountEffect } from 'hooks/useMountEffect'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Observable, Subject, Subscription, from } from 'rxjs'
import { mergeMap, takeUntil } from 'rxjs/operators'
import { addRecentSource, setFeedToSave, trackFeedInteraction } from 'services/content/recommended/actions'
import { userFeedsSelector, userSavedStreamsSelector, userStreamsSelector } from 'services/content/selectors'
import { createStream, deleteStream, getStreams, trackStreamInteraction } from 'services/content/streams/actions'
import { instanceOfStream } from 'services/content/util'
import { checkFeatureAvailability, checkProductLimit } from 'services/product'
import { FEATURE_FIND_MY_STREAMS_SAVE, LIMIT_SAVED_STREAMS } from 'shared/constants'
import { StoreState, StoreThunkDispatch } from 'store/state'
import * as FilterStore from 'utils/filter-store'
import CarouselView from './CarouselView'
import styles from './ContentLayout.pcss'
import { ContentLayoutHeader } from './ContentLayoutHeader'
import { ContentType, FilterType, PostDestination } from 'shared'

const isEqual = require('react-fast-compare')

export interface ContentLayoutProps {
  source: ContentSource
  items?: AnyContent[]
  loading?: boolean
  hidden?: boolean
  browseUrl?: string
  browseLinkText?: string
  navPrefix?: string
  expanded?: boolean
  filter?: FilterType
  range?: RangeFilter
  filtersBoxClassName?: string
  destinations?: { [key: string]: Readonly<PostDestination> }
  // disable auto tracking content interactions
  disableAutoInteractionsTracking?: boolean
  hideNavLink?: boolean
  defaultRange?: RangeFilter
  scroll?: ScrollConfiguration
  hideHeader?: boolean

  onExpanded?: (source: ContentSource) => void
  onCollapsed?: (source: ContentSource) => void
  onRangeChanged?: (range: RangeFilter) => void
  onFilterChanged?: (filter: FilterType) => void
  onContentItemClick?(content: ContentItem): void
  onContentItemCompose?(content: ContentItem): void
  onSaveSource?(source: ContentSource): void | boolean
}

interface ConnectedContentLayoutProps extends ContentLayoutProps {
  userStreams: Stream[]
  userFeeds: Feed[]
  savedStreamsCount: number
  trackFeedInteraction: (id: string) => Observable<string[]>
  trackStreamInteraction: (id: string) => Observable<string[]>
  saveStream: (name: string, id: string, color?: string) => Observable<any>
  deleteStream: (id: string) => Observable<string>
  setFeedToSave: (feed: Feed) => any
  getUserStreams: () => Observable<Stream[]>
  addRecentSource: (source: ContentSource) => any
  checkFeatureAvailability: (feature: string) => boolean
  checkProductLimit: (limitKey: string, value: number) => boolean
}

export function ContentLayout(props: ConnectedContentLayoutProps) {
  const intl = useIntl()
  const {
    source,
    disableAutoInteractionsTracking,
    trackStreamInteraction,
    trackFeedInteraction,
    addRecentSource,
    onFilterChanged: propsOnFilterChanged,
    onRangeChanged: propsOnRangeChanged,
    onContentItemCompose,
    onExpanded,
    onCollapsed,
    onSaveSource: propsOnSaveSource,
    setFeedToSave,
    deleteStream,
    checkFeatureAvailability,
    checkProductLimit,
    savedStreamsCount,
    userStreams,
    userFeeds,
    items,
    loading,
    browseUrl,
    browseLinkText,
    hideNavLink,
    navPrefix,
    filtersBoxClassName,
    destinations,
    onContentItemClick,
    defaultRange,
    hideHeader,
    hidden,
    scroll,
    filter: propsFilter,
    range: propsRange,
    expanded: propsExpanded,
    saveStream,
    getUserStreams
  } = props

  const isStream = useMemo(() => instanceOfStream(source), [source])

  const isSavedSource = useMemo(() => {
    const stream = source as Stream
    const feed = source as Feed
    const uniqueId = isStream
      ? stream.originalId || stream.id
      : feed.uniqueSource || feed.id

    return isStream
      ? !!userStreams.find(s => s.id === stream.id || s.originalId === uniqueId)
      : !!userFeeds.find(f => f.uniqueSource === uniqueId)
  }, [isStream, source, userStreams, userFeeds])

  const defaultFilters = useMemo(() => {
    if (isStream) {
      return FilterStore.getStreamFilters(source as Stream)
    }

    const storedFilters = FilterStore.getFeedFilters(source as Feed)
    if ((source as Feed).sources.includes(storedFilters.type as ContentType)) {
      return storedFilters
    }

    return {
      type: (source as Feed).sources[0],
      range: storedFilters.range
    }
  }, [isStream, source])

  const contentTypes = useMemo(() => {
    if (isStream) {
      return (source as Stream).filters
    }

    return (source as Feed).sources
  }, [isStream, source])

  const [expanded, setExpanded] = useState(!!propsExpanded)
  const [filter, setFilter] = useState(propsFilter || defaultFilters.type)
  const [range, setRange] = useState(propsRange || defaultFilters.range)
  const [saveLoading, setSaveLoading] = useState(false)
  const [lastTrackedSourceId, setLastTrackedSourceId] = useState<string | undefined>(undefined)

  const unmount$ = useRef(new Subject<boolean>())
  const trackViewInteraction$ = useRef<Subscription | undefined>(undefined)
  const deleteStream$ = useRef<Subscription | undefined>(undefined)
  const saveStream$ = useRef<Subscription | undefined>(undefined)
  const interactions = useRef<string[]>([])

  const updateFilters = useCallback((type: FilterType, rangeVal: RangeFilter) => {
    if (isStream) {
      FilterStore.setStreamFilters((source as Stream).id, type, rangeVal)
      return
    }

    FilterStore.setFeedFilters((source) as Feed, type, rangeVal)
  }, [isStream, source])

  const trackView = useCallback(() => {
    if (disableAutoInteractionsTracking) {
      return
    }

    const sourceId = isStream
      ? (source as Stream).originalId || source.id
      : (source as Feed).uniqueSource

    // Prevent multiple records of the same source
    if (interactions.current.includes(sourceId)) {
      return
    }

    const trackInteractionObservable = isStream
      ? trackStreamInteraction(source.id)
      : trackFeedInteraction(sourceId)

    addRecentSource(source)
    trackViewInteraction$.current = trackInteractionObservable.pipe(
      takeUntil(unmount$.current.asObservable()))
      .subscribe(() => {
        interactions.current.push(sourceId)
      })
  }, [isStream, source, disableAutoInteractionsTracking, trackStreamInteraction, trackFeedInteraction, addRecentSource])

  const onFilterChanged = useCallback((newFilter: FilterType) => {
    updateFilters(newFilter, range)
    setFilter(newFilter)
    if (propsOnFilterChanged) {
      propsOnFilterChanged(newFilter)
    }
  }, [updateFilters, range, propsOnFilterChanged])

  const onRangeChanged = useCallback((newRange: RangeFilter) => {
    updateFilters(filter, newRange)
    setRange(newRange)
    if (propsOnRangeChanged) {
      propsOnRangeChanged(newRange)
    }
  }, [updateFilters, filter, propsOnRangeChanged])

  const onItemShared = useCallback((item: AnyContent) => {
    if (onContentItemCompose) {
      onContentItemCompose(item as ContentItem)
    }
  }, [onContentItemCompose])

  const expand = useCallback(() => {
    setExpanded(true)
    if (onExpanded) {
      onExpanded(source)
    }
  }, [onExpanded, source])

  const collapse = useCallback(() => {
    setExpanded(false)
    if (onCollapsed) {
      onCollapsed(source)
    }
  }, [onCollapsed, source])

  const onSaveSource = useCallback(() => {
    if (propsOnSaveSource) {
      const abort = propsOnSaveSource(source)
      if (abort) {
        return
      }
    }

    const id = source.id
    if (!isStream) {
      setFeedToSave(source as Feed)
      return
    }

    if (isSavedSource) {
      const userStreamToDelete = userStreams.find(s => s.id === id || s.originalId === id)
      if (userStreamToDelete) {
        setSaveLoading(true)
        deleteStream$.current = deleteStream(userStreamToDelete.id).pipe(
          takeUntil(unmount$.current))
          .subscribe(() => setSaveLoading(false))
      }
      return
    }

    const saveStreamAllowed = checkFeatureAvailability(FEATURE_FIND_MY_STREAMS_SAVE)
    if (!saveStreamAllowed) {
      return
    }

    const withinLimit = checkProductLimit(LIMIT_SAVED_STREAMS, savedStreamsCount + 1)
    if (!withinLimit) {
      return
    }

    const stream = source as Stream
    setSaveLoading(true)
    saveStream$.current = saveStream(stream.title, stream.originalId || stream.id, stream.color)
      .pipe(
        mergeMap(getUserStreams),
        takeUntil(unmount$.current)
      )
      .subscribe(() => setSaveLoading(false))
  }, [
    isStream,
    isSavedSource,
    source,
    setFeedToSave,
    userStreams,
    deleteStream,
    checkFeatureAvailability,
    checkProductLimit,
    savedStreamsCount,
    saveStream,
    getUserStreams,
    propsOnSaveSource
  ])

  // Update expanded prop
  useEffect(() => {
    setExpanded(!!propsExpanded)
  }, [propsExpanded])

  // Update filter prop
  useEffect(() => {
    if (propsFilter && propsFilter !== filter) {
      setFilter(propsFilter)
    }
  }, [propsFilter, filter])

  // Update range prop
  useEffect(() => {
    if (propsRange && propsRange !== range) {
      setRange(propsRange)
    }
  }, [propsRange, range])

  // Track view on mount
  useMountEffect(() => {
    if (expanded) {
      trackView()
      setLastTrackedSourceId(source.id)
    }
  })

  // Track view on source change
  useEffect(() => {
    if (expanded && lastTrackedSourceId !== source.id) {
      trackView()
      setLastTrackedSourceId(source.id)
    }
  }, [source.id, expanded, lastTrackedSourceId, trackView])

  // Cleanup on unmount
  useEffect(() => {
    const unmountSubject = unmount$.current
    return () => {
      unmountSubject.next(true)

      if (trackViewInteraction$.current) {
        trackViewInteraction$.current.unsubscribe()
      }

      if (deleteStream$.current) {
        deleteStream$.current.unsubscribe()
      }

      if (saveStream$.current) {
        saveStream$.current.unsubscribe()
      }
    }
  }, [])

  const renderGrid = useCallback(() => {
    const showNewFeedNote = !isStream && (source as Feed).isNew

    return (
      <ContentGridView
        sourceId={
          (source as Feed).uniqueSource
          || (source as Stream).originalId
          || source.id
        }
        isStream={isStream}
        feedType={source?.type as any}
        filter={filter}
        filtersContainerClassName={filtersBoxClassName}
        range={range}
        contentTypes={contentTypes}
        errorElement={
          showNewFeedNote
            ? <NewFeedMessage className={styles['new-feed-msg']} />
            : undefined
        }
        onFilterChange={onFilterChanged}
        onRangeChanged={onRangeChanged}
        onContentItemClick={onContentItemClick}
        onItemShared={onItemShared}
        scroll={scroll}
        preventTopOverflow
      />
    )
  }, [
    isStream,
    source,
    filter,
    filtersBoxClassName,
    range,
    contentTypes,
    onFilterChanged,
    onRangeChanged,
    onContentItemClick,
    onItemShared,
    scroll
  ])

  if (hidden) {
    return null
  }

  const title = isStream ? (source as Stream).title : (source as Feed).name
  const defaultBrowseText = intl.formatMessage({ id: 'general.carousel.nav.see-all', defaultMessage: 'see all' })
  const browseText = browseLinkText || defaultBrowseText

  return (
    <div className={styles.wrapper} data-testid="content-layout">
      {!hideHeader && (
        <ContentLayoutHeader
          source={source}
          hideNavLink={hideNavLink}
          sourceSaved={isSavedSource}
          title={title}
          filter={filter}
          browseLinkText={browseText}
          backLinkText={intl.formatMessage({ id: 'general.carousel.nav.back', defaultMessage: 'back' })}
          browseUrl={browseUrl}
          prefix={navPrefix}
          saveLoading={saveLoading}
          contentTypes={contentTypes}
          expanded={expanded}
          onFilterChanged={onFilterChanged}
          onExpandLinkClick={expand}
          onBackLinkClick={collapse}
          onSave={onSaveSource}
        />
      )}

      {
        expanded
          ? renderGrid()
          : (
            <CarouselView
              source={source}
              items={items}
              loading={loading}
              defaultRange={defaultRange}
              filter={filter}
              key="list-view"
              destinations={destinations}
              onContentItemClick={onContentItemClick}
              onContentItemCompose={onContentItemCompose}
            />
          )
      }
    </div>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    userStreams: userStreamsSelector(state),
    userFeeds: userFeedsSelector(state),
    savedStreamsCount: userSavedStreamsSelector(state).length
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    trackFeedInteraction: (id: string) => {
      return from(dispatch(trackFeedInteraction({ feedIds: [id], event: INTERACTION_EVENT_OPEN })).unwrap())
    },
    trackStreamInteraction: (id: string) => {
      return from(dispatch(trackStreamInteraction({ streamIds: [id], event: INTERACTION_EVENT_OPEN })).unwrap())
    },
    saveStream: (name: string, id: string, color?: string) => from(dispatch(createStream({ name, id, color })).unwrap()),
    deleteStream: (id: string) => from(dispatch(deleteStream(id)).unwrap()),
    setFeedToSave: (feed: Feed) => dispatch(setFeedToSave(feed)),
    getUserStreams: () => from(dispatch(getStreams(true)).unwrap()),
    addRecentSource: (source: ContentSource) => dispatch(addRecentSource(source)),
    checkFeatureAvailability: (feature: string) => dispatch(checkFeatureAvailability(feature)),
    checkProductLimit: (limitKey: string, value: number) => dispatch(checkProductLimit(limitKey, value))
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ContentLayout)
